import { useAssetDeviationAnalysis } from 'ClientPortal/src/pages/Assets/AssetDetails/AssetDeviationAnalysis/AssetDeviationAnalysis.data'
import {
    CardItem,
    DeviationAnalysisExportCard,
    DeviationAnalysisWidget,
    EnergyInfoBox,
    getDeviationAnalysisExportCardCopy,
    getDeviationAnalysisExportCopy,
    getDeviationAnalysisWidgetCopy,
    MultiStructuresInfoBox,
    PageGridContainer,
    PageGridItem,
    tokens,
    WhiteEmptyStateImage,
} from 'ComponentLibrary'
import { Stack } from '@mui/material'
import { Typography } from 'ComponentLibrary/src/piazza'
import AssetDeviationAnalysisLoading from 'ClientPortal/src/pages/Assets/AssetDetails/AssetDeviationAnalysis/AssetDeviationAnalysis.load'
import { isEmpty } from 'lodash'
import { useTranslation } from 'react-i18next'
import { getMultiStructuresInfoBoxCopy } from 'ClientPortal/src/pages/Assets/AssetDetails/multiStructuresInfoBoxCopy'
import { getEnergyInfoBoxCopy } from 'ClientPortal/src/pages/Assets/AssetDetails/energyInfoBoxCopy'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

export default function AssetDeviationAnalysis() {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tDeviationAnalysisWidget } = useTranslation('terminology', { keyPrefix: 'common.deviationAnalysisWidget' })
    const { t: tExport } = useTranslation('terminology', { keyPrefix: 'export' })
    const { t: tAssetDeviationAnalysis } = useTranslation('pages', { keyPrefix: 'assets.assetDetails.assetDeviationAnalysis' })
    const locale = useCurrentResolvedLocale()
    const {
        deviationAnalysis,
        isDeviationAnalysisPending,
        baseValuation,
        comparisonValuation,
        setComparisonValuationId,
        setBaseValuationId,
        groupedOptionsByLabel,
        allStructures,
        assetEnergySummary,
        valuationOptions,
        isValuationsPending,
        isAssetEnergySummaryLoading,
        isAssetPending,
        showDeviationAnalysis,
        isEnergySummaryEnabled,
        hasGroupedValuations,
        assetTitle,
    } = useAssetDeviationAnalysis()
    const deviationAnalysisExportCardCopy = getDeviationAnalysisExportCardCopy(tExport)
    const deviationAnalysisExportCopy = getDeviationAnalysisExportCopy(tCommon, tExport)
    const deviationAnalysisWidgetCopy = getDeviationAnalysisWidgetCopy(
        deviationAnalysisExportCopy,
        tDeviationAnalysisWidget,
        comparisonValuation?.valuationDate ?? comparisonValuation?.date
    )
    const multiStructuresInfoBoxCopy = getMultiStructuresInfoBoxCopy(tCommon)
    const energyInfoBoxCopy = getEnergyInfoBoxCopy(tAssetDeviationAnalysis, tCommon)

    if (isValuationsPending || isAssetPending) {
        return <AssetDeviationAnalysisLoading />
    }

    if (!showDeviationAnalysis) {
        return (
            <PageGridContainer withBottomSpace withFooter>
                <PageGridItem columnSpan={6} columnSpanXs={4}>
                    <Stack
                        direction="column"
                        gap={tokens.SizingSpacing200}
                        height="264px"
                        alignItems="center"
                        justifyContent="center"
                        paddingTop={tokens.SizingSpacing200}
                    >
                        <WhiteEmptyStateImage height="120px" width="145px" />
                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                            {tAssetDeviationAnalysis('emptyStateTitle')}
                        </Typography>
                    </Stack>
                </PageGridItem>
            </PageGridContainer>
        )
    }

    return (
        <PageGridContainer background={tokens.ColorBackgroundDark}>
            <PageGridItem columnSpan={4} columnSpanXs={4}>
                <CardItem>
                    <DeviationAnalysisWidget
                        deviationAnalysis={deviationAnalysis}
                        isDeviationAnalysisPending={isDeviationAnalysisPending}
                        valuationOptions={valuationOptions}
                        groupedValuationOptions={groupedOptionsByLabel}
                        hasGroupedValuations={hasGroupedValuations}
                        setBaseValuationId={setBaseValuationId}
                        setComparisonValuationId={setComparisonValuationId}
                        baseValuation={baseValuation}
                        comparisonValuation={comparisonValuation}
                        copy={deviationAnalysisWidgetCopy}
                        locale={locale}
                    />
                </CardItem>
            </PageGridItem>
            <PageGridItem columnSpan={2} columnSpanXs={2}>
                <Stack direction={'column'} gap={tokens.SizingSpacing300}>
                    <DeviationAnalysisExportCard
                        baseValuation={baseValuation}
                        comparisonValuation={comparisonValuation}
                        deviationAnalysis={deviationAnalysis}
                        assetTitle={assetTitle}
                        copy={deviationAnalysisExportCardCopy}
                        exportCopy={deviationAnalysisExportCopy}
                        locale={locale}
                    />
                    {isEnergySummaryEnabled && (
                        <EnergyInfoBox
                            backgroundColor={tokens.ColorBackgroundLight}
                            assetEnergySummary={assetEnergySummary}
                            copy={energyInfoBoxCopy}
                            isAssetEnergySummaryPending={isAssetEnergySummaryLoading}
                            variant="spacious"
                        />
                    )}
                    {!isEmpty(allStructures) && <MultiStructuresInfoBox structures={allStructures} copy={multiStructuresInfoBoxCopy} />}
                </Stack>
            </PageGridItem>
        </PageGridContainer>
    )
}
